
import {
  hasIntersectionObserver,
  hasResizeObserver,
} from '~/utils/browserSupport'

export default {
  name: 'VehicleHeadlightRamp',
  props: {
    blocks: { type: Array, required: true },
  },
  data() {
    return {
      activeId: null,
      litIds: [],
      sectionObserver: null,
      ignitionObserver: null,
      resizeObserver: null,
      ignitionDone: false,
      reducedMotion: false,
      ignitionTimers: [],
      scrollFixTimer: null,
      activeGroup: 'exterior',
      canScrollLeft: false,
      canScrollRight: false,
      haloColors: {},
      stickyOffset: 0,
    }
  },
  computed: {
    items() {
      return this.blocks.map(block => {
        const image = block.headlight_type.images?.[0]
        return {
          id: block.headlight_type.id,
          name: block.headlight_type.name,
          anchorId: `feux-${
            this.$slugify(block.headlight_type.name).toLowerCase() ||
            block.headlight_type.id
          }`,
          iconSrc: image
            ? this.$cropUrl(image, 80, 80, image.extension, 86)
            : null,
          isInterior: !!block.headlight_type.is_interior,
        }
      })
    },
    exteriorItems() {
      return this.items.filter(item => !item.isInterior)
    },
    interiorItems() {
      return this.items.filter(item => item.isInterior)
    },
    groups() {
      const groups = []
      if (this.exteriorItems.length) {
        groups.push({
          key: 'exterior',
          label: this.$t('components.configurator.exteriorLights'),
          count: this.exteriorItems.length,
        })
      }
      if (this.interiorItems.length) {
        groups.push({
          key: 'interior',
          label: this.$t('components.configurator.interiorLights'),
          count: this.interiorItems.length,
        })
      }
      return groups
    },
    showTabs() {
      return this.groups.length > 1
    },
    visibleItems() {
      if (this.showTabs) {
        return this.activeGroup === 'interior' ? this.interiorItems : this.exteriorItems
      }
      return this.items
    },
  },
  watch: {
    activeId(newId) {
      if (!this.showTabs) return
      const item = this.items.find(i => i.id === newId)
      if (!item) return
      this.activeGroup = item.isInterior ? 'interior' : 'exterior'
      this.centerActiveItem()
    },
    activeGroup() {
      this.$nextTick(() => {
        if (this.$refs.strip) this.$refs.strip.scrollLeft = 0
        this.updateScrollFades()
      })
    },
  },
  mounted() {
    this.reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches

    if (hasIntersectionObserver()) {
      this.sectionObserver = new IntersectionObserver(
        entries => {
          const visible = entries.find(e => e.isIntersecting)
          if (visible) {
            const item = this.items.find(i => i.anchorId === visible.target.id)
            if (item) this.activeId = item.id
          }
        },
        { rootMargin: '-30% 0px -60% 0px' }
      )
      this.items.forEach(item => {
        const el = document.getElementById(item.anchorId)
        if (el) this.sectionObserver.observe(el)
      })
    }

    if (this.items.length && this.activeId === null) {
      this.activeId = this.items[0].id
    }

    this.measureStickyOffset()

    this.$nextTick(() => {
      this.updateScrollFades()
    })

    this.extractHaloColors()

    if (this.$refs.strip && hasResizeObserver()) {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateScrollFades()
      })
      this.resizeObserver.observe(this.$refs.strip)
    }

    if (this.reducedMotion || !hasIntersectionObserver()) {
      this.ignitionDone = true
      return
    }
    this.ignitionObserver = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting) && !this.ignitionDone) {
        this.ignitionDone = true
        this.runIgnition()
        this.ignitionObserver.disconnect()
      }
    })
    this.ignitionObserver.observe(this.$refs.ramp)
  },
  beforeDestroy() {
    this.sectionObserver?.disconnect()
    this.ignitionObserver?.disconnect()
    this.resizeObserver?.disconnect()
    this.ignitionTimers.forEach(clearTimeout)
    clearTimeout(this.scrollFixTimer)
  },
  methods: {
    measureStickyOffset() {
      const menu = document.querySelector('.vue-fixed-header')
      if (menu) {
        this.stickyOffset = menu.offsetHeight
        return
      }
      const header = document.querySelector('header.header')
      if (header && getComputedStyle(header).position === 'sticky') {
        this.stickyOffset = header.offsetHeight
      }
    },
    extractHaloColors() {
      this.items.forEach(item => {
        if (!item.iconSrc || this.haloColors[item.id]) return
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => {
          try {
            const size = 40
            const center = size / 2
            const canvas = document.createElement('canvas')
            canvas.width = size
            canvas.height = size
            const ctx = canvas.getContext('2d')
            ctx.drawImage(img, 0, 0, size, size)
            const pixels = ctx.getImageData(0, 0, size, size).data
            let r = 0
            let g = 0
            let b = 0
            let count = 0
            for (let radius = center * 0.76; radius <= center * 0.95; radius += 1.5) {
              for (let angle = 0; angle < 360; angle += 10) {
                const x = Math.round(center + radius * Math.cos((angle * Math.PI) / 180))
                const y = Math.round(center + radius * Math.sin((angle * Math.PI) / 180))
                const offset = (y * size + x) * 4
                const alpha = pixels[offset + 3]
                const sum = pixels[offset] + pixels[offset + 1] + pixels[offset + 2]
                if (alpha > 200 && sum > 90) {
                  r += pixels[offset]
                  g += pixels[offset + 1]
                  b += pixels[offset + 2]
                  count++
                }
              }
            }
            if (count >= 8) {
              let avgR = r / count
              let avgG = g / count
              let avgB = b / count
              const max = Math.max(avgR, avgG, avgB)
              if (max > 0 && max < 210) {
                const scale = 210 / max
                avgR = Math.min(255, avgR * scale)
                avgG = Math.min(255, avgG * scale)
                avgB = Math.min(255, avgB * scale)
              }
              this.$set(
                this.haloColors,
                item.id,
                `${Math.round(avgR)}, ${Math.round(avgG)}, ${Math.round(avgB)}`
              )
            }
          } catch (error) {}
        }
        img.src = item.iconSrc
      })
    },
    updateScrollFades() {
      const strip = this.$refs.strip
      if (!strip) return
      this.canScrollLeft = strip.scrollLeft > 2
      this.canScrollRight =
        strip.scrollLeft + strip.clientWidth < strip.scrollWidth - 2
    },
    scrollForward() {
      const strip = this.$refs.strip
      if (!strip) return
      strip.scrollBy({
        left: strip.clientWidth * 0.7,
        behavior: this.reducedMotion ? 'auto' : 'smooth',
      })
    },
    runIgnition() {
      this.visibleItems.forEach((item, index) => {
        this.ignitionTimers.push(
          setTimeout(() => {
            this.litIds.push(item.id)
            this.ignitionTimers.push(
              setTimeout(() => {
                this.litIds = this.litIds.filter(id => id !== item.id)
              }, 900)
            )
          }, index * 60)
        )
      })
    },
    scrollToBlock(item) {
      const el = document.getElementById(item.anchorId)
      if (!el) return
      this.activeId = item.id
      el.scrollIntoView({
        behavior: this.reducedMotion ? 'auto' : 'smooth',
        block: 'start',
      })
      clearTimeout(this.scrollFixTimer)
      this.scrollFixTimer = setTimeout(() => {
        if (Math.abs(el.getBoundingClientRect().top - 160) > 8) {
          el.scrollIntoView({ behavior: 'auto', block: 'start' })
        }
      }, this.reducedMotion ? 150 : 800)
    },
    centerActiveItem() {
      this.$nextTick(() => {
        const strip = this.$refs.strip
        const index = this.visibleItems.findIndex(
          item => item.id === this.activeId
        )
        const refs = this.$refs.rampItem || []
        const target = refs[index]
        const el = target && (target.$el || target)
        if (!strip || !el || typeof el.offsetLeft !== 'number') return
        strip.scrollTo({
          left: el.offsetLeft - (strip.clientWidth - el.offsetWidth) / 2,
          behavior: this.reducedMotion ? 'auto' : 'smooth',
        })
      })
    },
  },
}
